// The console's session: the refresh cookie lives with the browser (HttpOnly, on /api/auth), the
// short-lived access token only in memory. Every API call carries it, and it's renewed a minute
// before it expires.

export type Me = {
  user: { id: string; email: string };
  tenant: { id: string; slug: string; name: string };
  roles: string[];
  households: "all" | "list" | "none";
  household_ids: string[];
};

let accessToken: string | null = null;
let renewal: number | undefined;

export async function refresh(): Promise<boolean> {
  const response = await fetch("/api/auth/refresh", { method: "POST", credentials: "same-origin" });
  if (!response.ok) {
    accessToken = null;
    return false;
  }
  const body = (await response.json()) as { access_token: string; expires_in: number };
  accessToken = body.access_token;
  window.clearTimeout(renewal);
  renewal = window.setTimeout(refresh, Math.max(body.expires_in - 60, 10) * 1000);
  return true;
}

async function call(path: string, init: RequestInit = {}): Promise<Response> {
  const go = () =>
    fetch(path, {
      ...init,
      credentials: "same-origin",
      headers: { ...(init.headers ?? {}), Authorization: `Bearer ${accessToken ?? ""}` },
    });
  let response = await go();
  if (response.status === 401 && (await refresh())) {
    response = await go();
  }
  if (!response.ok) {
    let detail = await response.text();
    try {
      detail = (JSON.parse(detail) as { detail?: string }).detail ?? detail;
    } catch {
      // not JSON: keep the text
    }
    throw new Error(typeof detail === "string" ? detail : `${response.status}`);
  }
  return response;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  return (await (await call(path, init)).json()) as T;
}

// A change: POST (or PUT, DELETE) with a JSON body; the answer's JSON, if it has one.
export async function send<T = unknown>(path: string, body: unknown = {}, method = "POST"): Promise<T | null> {
  const response = await call(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: method === "DELETE" ? undefined : JSON.stringify(body),
  });
  const text = await response.text();
  return text ? (JSON.parse(text) as T) : null;
}

// A document's bytes (each view is audited by the API), opened in a new tab when the browser can
// show it safely, otherwise saved.
export async function openDocument(id: string, name: string): Promise<void> {
  const response = await call(`/api/documents/${id}/content`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const inline = /^(image\/(jpeg|png|gif|webp)|application\/pdf)$/.test(blob.type);
  const link = document.createElement("a");
  link.href = url;
  if (inline) {
    link.target = "_blank";
    link.rel = "noopener";
  } else {
    link.download = name || "document";
  }
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function signIn(next = window.location.pathname): void {
  window.location.assign(`/auth/google/start?next=${encodeURIComponent(next)}`);
}

export async function signOut(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
  accessToken = null;
  window.clearTimeout(renewal);
}

// Files: a multipart form (a receipt, the sheet's tabs); the answer's JSON.
export async function upload<T>(path: string, form: FormData): Promise<T> {
  return (await (await call(path, { method: "POST", body: form })).json()) as T;
}
